/**
 * A fail-closed "this must be deleted" marker that survives reloads.
 *
 * Deleting a remembered-device / Face ID record from IndexedDB can fail
 * (blocked open, quota/IO error). A record that survives a failed delete
 * must never unlock later — not in this tab, and not after a reload. So
 * before every delete we write a marker to localStorage (keyed by user id,
 * or "*" for "every user on this browser"); the marker holds NO key
 * material, just "1". It is cleared only after the delete is confirmed.
 * While a marker is set the unlock path refuses the record and retries the
 * delete. If localStorage itself is unavailable the marker falls back to
 * memory (this page only) — the best available.
 */
export const ALL_USERS = "*"

export function createRevocationMarker(prefix: string) {
  const memory = new Set<string>()
  const key = (scope: string) => `${prefix}:${scope}`

  function read(scope: string): boolean {
    if (memory.has(scope)) return true
    try {
      return localStorage.getItem(key(scope)) === "1"
    } catch {
      return false
    }
  }

  return {
    /** Mark `scope` (a user id, or ALL_USERS) as pending deletion. */
    mark(scope: string) {
      try {
        localStorage.setItem(key(scope), "1")
      } catch {
        memory.add(scope)
      }
    },
    /** Clear after a CONFIRMED delete of `scope`. */
    clear(scope: string) {
      memory.delete(scope)
      try {
        localStorage.removeItem(key(scope))
      } catch {
        // nothing persisted to clear
      }
    },
    /** Clear every marker (after a confirmed delete of every record). */
    clearAll() {
      memory.clear()
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i)
          if (k?.startsWith(`${prefix}:`)) localStorage.removeItem(k)
        }
      } catch {
        // nothing persisted to clear
      }
    },
    /** Is `userId`'s record (or every record) pending deletion? */
    isRevoked(userId: string): boolean {
      return read(ALL_USERS) || (!!userId && read(userId))
    },
    /** Is anything pending deletion? */
    any(): boolean {
      if (memory.size > 0) return true
      try {
        for (let i = 0; i < localStorage.length; i++) {
          if (localStorage.key(i)?.startsWith(`${prefix}:`)) return true
        }
      } catch {
        return false
      }
      return false
    },
    allRevoked: () => read(ALL_USERS),
  }
}
